import { type FormEvent, useEffect, useState } from "react";
import type { Locale } from "./i18n";
import type { MerchantLocation } from "./Locations";

type Copy = (key: string, values?: Record<string, string | number>) => string;

export interface PairingCodeReceipt {
  id: string;
  pairingCode: string;
  expiresAt: string;
}

export interface PairedDevice {
  id: string;
  label: string;
  status: "active" | "revoked";
  location: { id: string; displayName: string };
  lastSeenAt: string | null;
  createdAt: string;
}

interface PairingCodeInput {
  locationId: string;
  label: string;
}

type LocationState =
  | { status: "loading" }
  | { status: "error"; permission: boolean }
  | { status: "ready"; items: MerchantLocation[] };

type DeviceState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; items: PairedDevice[] };

export function normalizePairingCodeInput(
  locationId: string,
  label: string,
): PairingCodeInput | null {
  const normalizedLabel = label.trim();
  if (
    !/^[a-z0-9][a-z0-9_-]{2,127}$/.test(locationId) ||
    normalizedLabel.length === 0 ||
    normalizedLabel.length > 80
  ) {
    return null;
  }
  return { locationId, label: normalizedLabel };
}

export function StaffDevices({
  merchantId,
  canManage,
  locale,
  copy,
  initialLocations,
  initialDevices,
  initialReceipt,
}: {
  merchantId: string;
  canManage: boolean;
  locale: Locale;
  copy: Copy;
  initialLocations?: MerchantLocation[];
  initialDevices?: PairedDevice[];
  initialReceipt?: PairingCodeReceipt;
}) {
  const [locations, setLocations] = useState<LocationState>(
    initialLocations
      ? { status: "ready", items: initialLocations }
      : { status: "loading" },
  );
  const [devices, setDevices] = useState<DeviceState>(
    initialDevices
      ? { status: "ready", items: initialDevices }
      : { status: "loading" },
  );
  const [locationId, setLocationId] = useState(
    initialLocations?.find((location) => location.status === "active")?.id ?? "",
  );
  const [label, setLabel] = useState("");
  const [receipt, setReceipt] = useState<PairingCodeReceipt | null>(
    initialReceipt ?? null,
  );
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [pendingRevoke, setPendingRevoke] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState(false);

  useEffect(() => {
    if (initialLocations || !canManage) return;
    const controller = new AbortController();
    setLocations({ status: "loading" });
    fetch("/v1/merchant/locations", {
      credentials: "include",
      headers: { "accept-language": locale, "x-merchant-id": merchantId },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          setLocations({ status: "error", permission: response.status === 401 || response.status === 403 });
          return;
        }
        const body = (await response.json()) as { items?: MerchantLocation[] };
        const items = Array.isArray(body.items) ? body.items : [];
        setLocations({ status: "ready", items });
        setLocationId((current) => current || items.find((location) => location.status === "active")?.id || "");
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setLocations({ status: "error", permission: false });
        }
      });
    return () => controller.abort();
  }, [canManage, initialLocations, locale, merchantId, reloadKey]);

  useEffect(() => {
    if (initialDevices || !canManage) return;
    const controller = new AbortController();
    setDevices({ status: "loading" });
    fetch("/v1/merchant/devices", {
      credentials: "include",
      headers: { "accept-language": locale, "x-merchant-id": merchantId },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          setDevices({ status: "error" });
          return;
        }
        const body = (await response.json()) as { items?: PairedDevice[] };
        setDevices({ status: "ready", items: Array.isArray(body.items) ? body.items : [] });
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) setDevices({ status: "error" });
      });
    return () => controller.abort();
  }, [canManage, initialDevices, locale, merchantId, reloadKey]);

  const createPairingCode = async (event: FormEvent) => {
    event.preventDefault();
    const input = normalizePairingCodeInput(locationId, label);
    if (!input) {
      setCreateError(true);
      return;
    }
    setCreating(true);
    setCreateError(false);
    setCopied(false);
    try {
      const response = await fetch("/v1/merchant/device-pairing-codes", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", "x-merchant-id": merchantId },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        setCreateError(true);
        return;
      }
      const nextReceipt = (await response.json()) as PairingCodeReceipt;
      if (!/^[A-Z2-9]{8}$/.test(nextReceipt.pairingCode) || !Number.isFinite(Date.parse(nextReceipt.expiresAt))) {
        setCreateError(true);
        return;
      }
      setReceipt(nextReceipt);
    } catch {
      setCreateError(true);
    } finally {
      setCreating(false);
    }
  };

  const copyPairingCode = async () => {
    if (!receipt) return;
    try {
      await navigator.clipboard.writeText(receipt.pairingCode);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const revokeDevice = async (deviceId: string) => {
    setRevoking(true);
    setRevokeError(false);
    try {
      const response = await fetch(`/v1/merchant/devices/${encodeURIComponent(deviceId)}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "x-merchant-id": merchantId },
      });
      if (!response.ok) {
        setRevokeError(true);
        return;
      }
      setDevices((current) =>
        current.status === "ready"
          ? { status: "ready", items: current.items.map((item) => item.id === deviceId ? { ...item, status: "revoked" } : item) }
          : current,
      );
      setPendingRevoke(null);
    } catch {
      setRevokeError(true);
    } finally {
      setRevoking(false);
    }
  };

  if (!canManage) {
    return (
      <section className="panel empty-state" role="status">
        <h2>{copy("merchant.locations.permissionTitle")}</h2>
        <p>{copy("merchant.locations.permissionBody")}</p>
      </section>
    );
  }
  if (locations.status === "loading") {
    return <section className="panel empty-state" aria-busy="true"><p>{copy("merchant.locations.loading")}</p></section>;
  }
  if (locations.status === "error") {
    return (
      <section className="panel empty-state" role="alert">
        <h2>{copy(locations.permission ? "merchant.locations.permissionTitle" : "merchant.locations.errorTitle")}</h2>
        <p>{copy(locations.permission ? "merchant.locations.permissionBody" : "merchant.locations.errorBody")}</p>
        {!locations.permission && <button className="quiet-button" type="button" onClick={() => setReloadKey((value) => value + 1)}>{copy("common.retry")}</button>}
      </section>
    );
  }

  const activeLocations = locations.items.filter((location) => location.status === "active");
  if (activeLocations.length === 0) {
    return <section className="panel empty-state" role="status"><h2>{copy("merchant.locations.emptyTitle")}</h2><p>{copy("merchant.staff.noLocations")}</p></section>;
  }

  return (
    <div className="staff-devices">
      <section className="management-access-note">
        <span className="access-note-mark" aria-hidden="true" />
        <div><h2>{copy("merchant.staff.managementAccess")}</h2><p>{copy("merchant.staff.managementAccessBody")}</p></div>
      </section>

      <section className="till-roster">
        <div className="section-heading">
          <div><p className="eyebrow">{copy("merchant.staff.tillsEyebrow")}</p><h2>{copy("merchant.staff.tillsTitle")}</h2><p>{copy("merchant.staff.tillsBody")}</p></div>
          <span className="roster-count">{copy("merchant.staff.activeCount", { count: devices.status === "ready" ? devices.items.filter((item) => item.status === "active").length : 0 })}</span>
        </div>
        {devices.status === "loading" ? (
          <div className="device-list-state" aria-busy="true">{copy("merchant.staff.loadingDevices")}</div>
        ) : devices.status === "error" ? (
          <div className="device-list-state" role="alert"><p>{copy("merchant.staff.devicesError")}</p><button className="quiet-button" type="button" onClick={() => setReloadKey((value) => value + 1)}>{copy("common.retry")}</button></div>
        ) : devices.items.length === 0 ? (
          <div className="device-list-state"><h3>{copy("merchant.staff.emptyTills")}</h3><p>{copy("merchant.staff.emptyTillsBody")}</p></div>
        ) : (
          <div className="device-list">
            {devices.items.map((device) => (
              <article className="device-row" key={device.id}>
                <span className={`device-status ${device.status}`} aria-hidden="true" />
                <div className="device-primary"><strong>{device.label}</strong><span>{device.location.displayName}</span></div>
                <div className="device-seen"><span>{copy(`merchant.staff.status.${device.status}`)}</span><time dateTime={device.lastSeenAt ?? device.createdAt}>{device.lastSeenAt ? copy("merchant.staff.lastSeen", { time: new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(device.lastSeenAt)) }) : copy("merchant.staff.neverUsed")}</time></div>
                {device.status === "active" && pendingRevoke !== device.id && <button className="text-button revoke-device" type="button" onClick={() => { setPendingRevoke(device.id); setRevokeError(false); }}>{copy("merchant.staff.revoke")}</button>}
                {pendingRevoke === device.id && (
                  <div className="revoke-confirmation">
                    <span>{copy("merchant.staff.revokeConfirm")}</span>
                    <button className="danger-button" type="button" disabled={revoking} onClick={() => void revokeDevice(device.id)}>{copy("merchant.staff.revokeAction")}</button>
                    <button className="text-button" type="button" disabled={revoking} onClick={() => setPendingRevoke(null)}>{copy("common.cancel")}</button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
        {revokeError && <p className="form-error" role="alert">{copy("merchant.staff.revokeError")}</p>}
      </section>

      <section className="pair-till-section">
        <div className="section-heading"><div><p className="eyebrow">{copy("merchant.staff.pairEyebrow")}</p><h2>{copy("merchant.staff.pair")}</h2><p>{copy("merchant.staff.pairBody")}</p></div></div>
        {receipt ? (
          <div className="pairing-code-card" aria-live="polite">
            <p className="eyebrow">{copy("merchant.staff.codeTitle")}</p>
            <output className="pairing-code">{receipt.pairingCode}</output>
            <p>{copy("merchant.staff.codeBody")}</p>
            <p className="pairing-expiry">{copy("merchant.staff.expiresAt", { time: new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(receipt.expiresAt)) })}</p>
            <div className="pairing-actions">
              <button className="primary-button" type="button" onClick={() => void copyPairingCode()}>{copy(copied ? "merchant.staff.copied" : "merchant.staff.copyCode")}</button>
              <button className="quiet-button" type="button" onClick={() => { setReceipt(null); setCopied(false); setCreateError(false); }}>{copy("merchant.staff.generateAnother")}</button>
            </div>
          </div>
        ) : (
          <form className="pairing-generator" onSubmit={createPairingCode}>
            <label htmlFor="pairing-location">{copy("merchant.nav.locations")}</label>
            <select id="pairing-location" value={locationId} onChange={(event) => setLocationId(event.target.value)} required>
              {activeLocations.map((location) => <option key={location.id} value={location.id}>{location.displayName}</option>)}
            </select>
            <label htmlFor="pairing-device-label">{copy("merchant.staff.deviceLabel")}</label>
            <input id="pairing-device-label" value={label} maxLength={80} onChange={(event) => setLabel(event.target.value)} required />
            <p className="field-help">{copy("merchant.staff.deviceLabelHelp")}</p>
            {createError && <p className="form-error" role="alert">{copy("merchant.staff.createError")}</p>}
            <button className="primary-button" type="submit" disabled={creating || label.trim().length === 0}>{copy(creating ? "merchant.staff.creatingCode" : "merchant.staff.createCode")}</button>
          </form>
        )}
      </section>
    </div>
  );
}
